import { useTranslation } from 'react-i18next'

import { deactivateRentalPortalListing } from '../../../../../api'
import { useNotification } from '../../../../../hooks'
import { stopPropagation } from '../../../../../utils/events'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  getIsListingChangedConflictError,
  getListingActionDisabledTooltipKey,
  getListingArchiveDescription,
  getListingDeactivateConflictNotification,
  getListingUnitLabel,
  LISTING_ACTION_ARCHIVE,
  LISTING_ACTION_DEACTIVATE,
  LISTING_ACTION_EDIT,
} from '../../../../RentalPortalListing'
import { getListingLivePortalNames } from '../../RentalPortalListings.utils'
import { useEditRentalPortalListing } from '../useEditRentalPortalListing'

import { getListingStatusActionErrorText } from './useRentalPortalListingsTableActions.utils'

import ArchiveIcon from '../../../../../icons/delete.svg'

export function useRentalPortalListingsTableActions({
  setModal,
  onClose,
  onPerformAction,
  onReload,
}) {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const { handleOpenEdit } = useEditRentalPortalListing({
    setModal,
    onClose,
    onReload,
  })

  function showConfirmModal(title, onOk, btnText, submitIcon) {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitProps: { beforeIconProps: { Icon: submitIcon } },
          submitText: btnText,
        }}
        description={title}
        onCancel={onClose}
        onSubmit={onOk}
      />
    )
  }

  function finishItemAction(notificationTitleKey) {
    onClose()

    handleAddSuccessNotification(notificationTitleKey)

    onReload?.()
  }

  async function deactivateItem({ item, onRetry }) {
    try {
      await deactivateRentalPortalListing({
        id: item.id,
        version: item.version,
      })

      finishItemAction('RentalPortal.ListingDeactivatedNotificationKey')
    } catch (error) {
      onClose()

      if (getIsListingChangedConflictError(error)) {
        handleAddInfoNotification(
          getListingDeactivateConflictNotification({ error, t })
        )

        onReload?.()
      } else {
        handleAddErrorNotification({
          textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
          title: getListingStatusActionErrorText({ error, t }),
          onClickTextButton: onRetry,
        })
      }
    }
  }

  function getHandleDeactivate(item) {
    return event => {
      stopPropagation(event)

      return deactivateItem({
        item,
        onRetry: () => setItemAction(LISTING_ACTION_DEACTIVATE, item),
      })
    }
  }

  function getHandleArchive(item) {
    return event => {
      stopPropagation(event)

      return onPerformAction({
        action: LISTING_ACTION_ARCHIVE,
        ids: [item.id],
      })
    }
  }

  function setItemAction(action, item) {
    if (getListingActionDisabledTooltipKey({ action, item })) {
      return null
    }

    switch (action) {
      case LISTING_ACTION_EDIT:
        handleOpenEdit(item)
        break
      case LISTING_ACTION_DEACTIVATE:
        showConfirmModal(
          t('RentalPortal.ListingDeactivateConfirmationTextKey', {
            name: getListingUnitLabel({ listing: item, t }),
          }),
          getHandleDeactivate(item),
          t('RentalPortal.ListingDeactivateBtnKey')
        )
        break
      case LISTING_ACTION_ARCHIVE:
        showConfirmModal(
          getListingArchiveDescription({
            livePortalNames: getListingLivePortalNames(item),
            name: getListingUnitLabel({ listing: item, t }),
            t,
          }),
          getHandleArchive(item),
          t('RentalPortal.ListingArchiveBtnKey'),
          ArchiveIcon
        )
        break
      default:
        return null
    }
  }

  return { setItemAction }
}
